import { Link } from "react-router-dom";
import { ArrowRight, MapPinned, GraduationCap, Upload, Video, Building2, Stamp, Languages } from "lucide-react";
import { PageHero } from "../components/site/PageHero";
import { SectionHeading } from "../components/site/SectionHeading";
import { Reveal } from "../components/site/Reveal";
import { ProcessTabs } from "../components/site/ProcessTabs";
import { FaqList } from "../components/site/FaqList";
import { CtaBand } from "../components/site/CtaBand";
import { FAQ_CANDIDATE } from "../content/site";
import { useSeo } from "../lib/useSeo";

const POINTS = [
  { icon: MapPinned, t: "Was und wo möchten Sie arbeiten?", d: "Sagen Sie uns, welche Tätigkeit Sie suchen und in welcher Region Deutschlands Sie gerne arbeiten möchten. Wenn Ihnen der Ort egal ist, ist das auch in Ordnung." },
  { icon: GraduationCap, t: "Was haben Sie gelernt?", d: "Beschreiben Sie Ihre Ausbildung, Ihren Abschluss und Ihre Berufserfahrung. Auch Führerscheine, Zertifikate und Sprachkenntnisse sind wichtig." },
  { icon: Upload, t: "So reichen Sie Unterlagen ein", d: "Sie können Ihren Lebenslauf und berufliche Nachweise direkt im Formular hochladen (PDF, JPG, PNG oder DOCX). Eine Passkopie oder Gesundheitsunterlagen brauchen wir am Anfang nicht." },
  { icon: Video, t: "Vorstellung bei Arbeitgebern", d: "Passt Ihr Profil zu einer Stelle, stellen wir Sie dem Arbeitgeber vor. Oft findet das erste Gespräch per Video statt. Wir helfen Ihnen bei der Vorbereitung." },
  { icon: Building2, t: "Der Arbeitgeber entscheidet", d: "Ob es zu einem Vorstellungsgespräch und einer Einstellung kommt, entscheidet allein das jeweilige Unternehmen. Wir können keine Einstellung garantieren." },
  { icon: Stamp, t: "Visum und Anerkennung", d: "Vor dem Arbeitsbeginn müssen das erforderliche Visum, die Beschäftigungserlaubnis und – je nach Beruf – die Anerkennung Ihres Abschlusses geklärt sein. Darüber entscheiden die Behörden." },
];

export default function Candidates() {
  useSeo("Für Bewerber", "Sie möchten in Deutschland arbeiten? Registrieren Sie sich kostenlos bei PACTORA Personalpartner. Wir stellen Sie passenden Arbeitgebern vor und begleiten die nächsten Schritte.", "/bewerber");
  return (
    <>
      <PageHero testid="candidates-hero" eyebrow="Für Bewerber" lines={["Arbeiten in Deutschland.", "Wir begleiten Sie."]}
        intro="Sie möchten in Deutschland arbeiten und bringen eine Ausbildung oder Berufserfahrung mit? Registrieren Sie sich kostenlos. Wir suchen für Sie Arbeitgeber, die zu Ihrem Profil passen, und erklären Ihnen jeden Schritt verständlich."
        image="/img/gastro.webp" imageAlt="Symbolbild: Koch in einer Restaurantküche">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-6">
          <Link to="/bewerber-registrierung" className="group inline-flex items-center justify-center gap-3 rounded-full bg-[color:var(--navy)] px-7 py-4 font-semibold text-white hover:bg-[color:var(--navy-deep)]" data-testid="candidates-hero-cta">Als Bewerber registrieren <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" /></Link>
          <Link to="/en/applicants" hrefLang="en" lang="en" className="inline-flex items-center gap-2 text-sm font-semibold text-[color:var(--teal-ink)] underline-offset-4 hover:underline" data-testid="candidates-switch-english"><Languages size={16} /> English version</Link>
        </div>
      </PageHero>

      <section className="bg-[color:var(--mist)] py-24 md:py-32" data-testid="candidates-points">
        <div className="container-x">
          <SectionHeading eyebrow="So funktioniert es" title="Was Sie wissen sollten" />
          <ol className="mt-14 grid gap-5 md:grid-cols-2">
            {POINTS.map((p, i) => (
              <Reveal key={p.t} as="li" delay={(i % 2) * 0.06}>
                <article className="flex h-full gap-5 rounded-3xl bg-white p-7 md:p-9" data-testid={`candidate-point-${i}`}>
                  <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-[color:var(--teal)] text-white"><p.icon size={22} /></span>
                  <div>
                    <h3 className="text-lg font-bold text-[color:var(--navy)]">{p.t}</h3>
                    <p className="mt-2 leading-relaxed text-slate-600">{p.d}</p>
                  </div>
                </article>
              </Reveal>
            ))}
          </ol>
          <Reveal>
            <div className="mt-10 rounded-3xl border border-[color:var(--teal)]/40 bg-white p-7 md:p-9">
              <h3 className="font-bold text-[color:var(--navy)]">Ehrlich gesagt</h3>
              <p className="mt-2 leading-relaxed text-slate-600">
                Wir versprechen kein garantiertes Visum, keine garantierte Einstellung und keine festen Bearbeitungszeiten. Welche Deutschkenntnisse nötig sind, hängt vom Beruf und vom Arbeitgeber ab. In vielen Berufen, zum Beispiel in der Pflege, sind bestimmte Sprachnachweise gesetzlich vorgeschrieben. Wir sagen Ihnen offen, was für Ihren Fall gilt.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-24 md:py-32" data-testid="candidates-process">
        <div className="container-x">
          <SectionHeading eyebrow="Ihr Weg" title="Von der Registrierung bis zum Arbeitsbeginn" className="mb-12" />
          <ProcessTabs initial="candidate" />
        </div>
      </section>

      <section className="bg-[color:var(--mist)] py-24 md:py-32" data-testid="candidates-faq">
        <div className="container-x grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4"><SectionHeading eyebrow="Fragen von Bewerbern" title="Häufige Fragen" /></div>
          <div className="lg:col-span-8"><FaqList items={FAQ_CANDIDATE} prefix="candidates-faq" /></div>
        </div>
      </section>
      <CtaBand />
    </>
  );
}
